import {useEffect, useMemo, useState} from 'react'
import {AlertTriangle, Plus, Settings2, Trash2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {Checkbox} from '@/components/ui/checkbox'
import {Input} from '@/components/ui/input'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {EnumSelectItems} from '@/components/shared/EnumSelectItems'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {MutedText} from '@/components/shared/MutedText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {TransformParamsFields} from '@/components/shared/TransformParamsFields'
import {validateTransformArgs} from '@/components/shared/transform-args'
import {useUpdateInboundAttributeMappings, useUpdateConnectorSettings} from '@/hooks/use-inbound-connectors'
import {useAttributeDefinitions} from '@/features/onboarding/hooks/useAttributeDefinitions'
import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import {getCurrentLanguage} from '@/i18n'
import type {TypeValue} from '@/types/types'
import type {InboundAttributeMapping, InboundConnectorSettings} from '@/types/inbound-connector'
import {
    TargetEntityType,
    InboundMappingType,
    InboundMappingTypeBadge,
    AttributeMappingTransformBadge,
} from '@/types/inbound-connector'

const MAX_MAPPINGS = 50
const MAX_SOURCE_LENGTH = 500
const MAX_TARGET_LENGTH = 100

const TRANSFORM_NONE = '__none__'


// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

interface MappingErrors {
    source?: string
    target?: string
    transformArgs?: string
}

function validateSource(source: string, i18n: ReturnType<typeof useI18n>): string | undefined {
    if (!source || source.trim().length === 0) return i18n.inboundAttributeMappings.sourceRequired
    if (source.length > MAX_SOURCE_LENGTH) return i18n.inboundAttributeMappings.sourceExceedsMaxLength
    return undefined
}

function validateTarget(target: string, i18n: ReturnType<typeof useI18n>): string | undefined {
    if (!target || target.trim().length === 0) return i18n.inboundAttributeMappings.targetRequired
    if (target.length > MAX_TARGET_LENGTH) return i18n.inboundAttributeMappings.targetExceedsMaxLength
    return undefined
}

function validateComputedTemplate(source: string, i18n: ReturnType<typeof useI18n>): string | undefined {
    if (/\{\{/.test(source) || /\}\}/.test(source)) {
        return i18n.inboundAttributeMappings.invalidTemplateSyntax
    }
    let depth = 0
    for (const ch of source) {
        if (ch === '{') depth++
        else if (ch === '}') depth--
        if (depth < 0) return i18n.inboundAttributeMappings.invalidTemplateSyntax
    }
    if (depth !== 0) return i18n.inboundAttributeMappings.invalidTemplateSyntax
    return undefined
}

// ---------------------------------------------------------------------------
// Warning banner
// ---------------------------------------------------------------------------

function MappingWarningBanner({action, testId}: {action?: React.ReactNode; testId?: string}) {
    const i18n = useI18n()
    return (
        <InfoBanner
            icon={AlertTriangle}

            className="rounded-lg border border-warning/40 bg-warning/10 text-foreground"
            data-testid={testId}
        >
            <div>
                <p className="font-medium text-sm">{i18n.inboundAttributeMappings.noMappings}</p>
                <MutedText size="sm" className="mt-1">{i18n.inboundAttributeMappings.noMappingsWarning}</MutedText>
            </div>
            {action}
        </InfoBanner>
    )
}

// ---------------------------------------------------------------------------
// Read-only component
// ---------------------------------------------------------------------------

interface InboundAttributeMappingReadOnlyProps {
    attributeMappings: InboundAttributeMapping[] | null
}

export function InboundAttributeMappingReadOnly({attributeMappings}: InboundAttributeMappingReadOnlyProps) {
    const i18n = useI18n()

    if (!attributeMappings || attributeMappings.length === 0) {
        return <MappingWarningBanner testId="readonly-warning-banner"/>
    }

    return (
        <div className="space-y-2" data-testid="inbound-attribute-mapping-readonly">
            {attributeMappings.map((m, idx) => (
                <div
                    key={idx}
                    className="flex items-center gap-4 py-2 border-b last:border-b-0 text-sm"
                    data-testid={`readonly-row-${idx}`}
                >
                    <MutedText size="sm" className="w-20 shrink-0" data-testid={`readonly-entity-type-${idx}`}>
                        {m.targetEntityType}
                    </MutedText>
                    <span className="flex-1 font-mono" data-testid={`readonly-source-${idx}`}>{m.source}</span>
                    <MutedText size="sm">→</MutedText>
                    <span className="flex-1 font-mono" data-testid={`readonly-target-${idx}`}>{m.target}</span>
                    {m.transform && (
                        <MutedText size="xs" data-testid={`readonly-transform-${idx}`}>{m.transform}</MutedText>
                    )}
                    {m.required && (
                        <MutedText size="xs" data-testid={`readonly-required-${idx}`}>
                            {i18n.inboundAttributeMappings.required}
                        </MutedText>
                    )}
                </div>
            ))}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Editor component
// ---------------------------------------------------------------------------

interface InboundAttributeMappingEditorProps {
    connectorId: string
    attributeMappings: InboundAttributeMapping[] | null
    canUpdate: boolean
    partyTypeCode: string
    identityTypeCode: string
    createParty: boolean
    createIdentity: boolean
    createAccount: boolean
    externalIdField: string
    lifecycleSourceField: string
    lifecycleEnabledValue: string
    connectorVersion: number
    currentSettings: InboundConnectorSettings
    onDirtyChange?: (dirty: boolean) => void
}

export function InboundAttributeMappingEditor({
    connectorId,
    attributeMappings,
    canUpdate,
    partyTypeCode,
    identityTypeCode,
    createParty: initialCreateParty,
    createIdentity: initialCreateIdentity,
    createAccount: initialCreateAccount,
    externalIdField: initialExternalIdField,
    lifecycleSourceField: initialLifecycleSourceField,
    lifecycleEnabledValue: initialLifecycleEnabledValue,
    connectorVersion,
    currentSettings,
    onDirtyChange,
}: InboundAttributeMappingEditorProps) {
    const i18n = useI18n()

    const [savedMappings, setSavedMappings] = useState<InboundAttributeMapping[]>(attributeMappings ?? [])
    const [currentMappings, setCurrentMappings] = useState<InboundAttributeMapping[]>(attributeMappings ?? [])
    const [createParty, setCreateParty] = useState(initialCreateParty)
    const [createIdentity, setCreateIdentity] = useState(initialCreateIdentity)
    const [createAccount, setCreateAccount] = useState(initialCreateAccount)
    const [partyTypeCodeState, setPartyTypeCodeState] = useState(partyTypeCode)
    const [identityTypeCodeState, setIdentityTypeCodeState] = useState(identityTypeCode)
    const [externalIdFieldState, setExternalIdFieldState] = useState(initialExternalIdField)
    const [lifecycleSourceFieldState, setLifecycleSourceFieldState] = useState(initialLifecycleSourceField)
    const [lifecycleEnabledValueState, setLifecycleEnabledValueState] = useState(initialLifecycleEnabledValue)

    const services = useServices()
    const partyTypesQuery = useQuery({
        queryKey: ['types', 'party_types'],
        // eslint-disable-next-line i18next/no-literal-string -- DB table name, not user-visible text
        queryFn: () => services.types.detail('party_types'),
    })
    const identityTypesQuery = useQuery({
        queryKey: ['types', 'identity_types'],
        // eslint-disable-next-line i18next/no-literal-string -- DB table name, not user-visible text
        queryFn: () => services.types.detail('identity_types'),
    })
    const identifierTypesQuery = useQuery({
        queryKey: ['types', 'identifier_types'],
        // eslint-disable-next-line i18next/no-literal-string -- DB table name, not user-visible text
        queryFn: () => services.types.detail('identifier_types'),
    })
    const partyTypeOptions = (partyTypesQuery.data?.values ?? []).filter((v) => v.active)
    const identityTypeOptions = (identityTypesQuery.data?.values ?? []).filter((v) => v.active)
    const identifierTypeOptions = (identifierTypesQuery.data?.values ?? []).filter((v) => v.active)
    const labelForType = (t: TypeValue): string => t.translations?.[getCurrentLanguage()]?.label ?? t.code

    const togglesDirty =
        createParty !== initialCreateParty ||
        createIdentity !== initialCreateIdentity ||
        createAccount !== initialCreateAccount ||
        partyTypeCodeState !== partyTypeCode ||
        identityTypeCodeState !== identityTypeCode ||
        externalIdFieldState !== initialExternalIdField ||
        lifecycleSourceFieldState !== initialLifecycleSourceField ||
        lifecycleEnabledValueState !== initialLifecycleEnabledValue

    const isDirty = useMemo(
        () => togglesDirty || JSON.stringify(currentMappings) !== JSON.stringify(savedMappings),
        [togglesDirty, currentMappings, savedMappings],
    )

    useEffect(() => {
        onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    // Schema-aware target picker — fetch the attribute definitions scoped to the connector's
    // configured PartyType / IdentityType. The dropdown is suggestions-only (free entry still
    // accepted), and rows whose target isn't in the schema get a soft warning badge.
    const {definitions: partyDefs} = useAttributeDefinitions('PARTY', partyTypeCode)
    const {definitions: identityDefs} = useAttributeDefinitions('IDENTITY', identityTypeCode)
    const partyAttrNames = useMemo(() => new Set(partyDefs.map((d) => d.name)), [partyDefs])
    const identityAttrNames = useMemo(() => new Set(identityDefs.map((d) => d.name)), [identityDefs])

    const isTargetInSchema = (m: InboundAttributeMapping): boolean => {
        if (!m.target) return true // empty target is a separate validation error, not a schema warning
        if (m.targetEntityType === TargetEntityType.PARTY) return partyAttrNames.has(m.target)
        if (m.targetEntityType === TargetEntityType.IDENTITY && !m.identifierType) return identityAttrNames.has(m.target)
        return true // RESOURCE / FUNCTION_GROUP / IDENTITY-with-identifierType: schema check not applicable
    }

    // ---------------------------------------------------------------------------
    // Validation
    // ---------------------------------------------------------------------------
    const rowErrors: MappingErrors[] = useMemo(
        () =>
            currentMappings.map((m) => {
                const sourceErr = validateSource(m.source, i18n)
                const templateErr =
                    !sourceErr && m.type === InboundMappingType.COMPUTED
                        ? validateComputedTemplate(m.source, i18n)
                        : undefined
                return {
                    source: sourceErr ?? templateErr,
                    target: validateTarget(m.target, i18n),
                    transformArgs: validateTransformArgs(m.transform, m.transformArgs, i18n),
                }
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentMappings],
    )

    const hasErrors = rowErrors.some((e) => e.source || e.target || e.transformArgs)

    // ---------------------------------------------------------------------------
    // Row management
    // ---------------------------------------------------------------------------
    const addMapping = (entityType: string, identifierType: string | null = null) => {
        if (currentMappings.length >= MAX_MAPPINGS) return
        const newMapping: InboundAttributeMapping = {
            source: '',
            target: '',
            targetEntityType: entityType,
            identifierType,
            transform: null,
            type: InboundMappingType.DIRECT,
            required: false,
            transformArgs: null,
        }
        setCurrentMappings((prev) => [...prev, newMapping])
    }
    const addPartyMapping = () => addMapping(TargetEntityType.PARTY)
    const addIdentityAttribute = () => addMapping(TargetEntityType.IDENTITY)
    const addIdentityIdentifier = () => addMapping(TargetEntityType.IDENTITY, '')

    const removeMapping = (idx: number) => {
        setCurrentMappings((prev) => prev.filter((_, i) => i !== idx))
    }

    const updateMapping = (idx: number, updates: Partial<InboundAttributeMapping>) => {
        setCurrentMappings((prev) =>
            prev.map((m, i) => (i === idx ? {...m, ...updates} : m)),
        )
    }

    const handleTypeChange = (idx: number, newType: string) => {
        updateMapping(idx, {type: newType as typeof InboundMappingType[keyof typeof InboundMappingType], source: ''})
    }

    // ---------------------------------------------------------------------------
    // Mutations — saving toggles + mappings together
    // ---------------------------------------------------------------------------
    const mutation = useUpdateInboundAttributeMappings(connectorId)
    const settingsMutation = useUpdateConnectorSettings(connectorId)
    const isSaving = mutation.isPending || settingsMutation.isPending

    const handleSave = async () => {
        if (hasErrors || isSaving) return
        const tasks: Promise<unknown>[] = []
        if (togglesDirty) {
            tasks.push(settingsMutation.mutateAsync({
                safeguards: currentSettings.safeguards,
                validation: currentSettings.validation,
                batchSize: currentSettings.batchSize,
                entityOperations: currentSettings.entityOperations,
                rehirePolicy: currentSettings.rehirePolicy,
                partyTypeCode: partyTypeCodeState,
                identityTypeCode: identityTypeCodeState,
                createParty,
                createIdentity,
                createAccount,
                externalIdField: externalIdFieldState,
                lifecycleSourceField: lifecycleSourceFieldState,
                lifecycleEnabledValue: lifecycleEnabledValueState,
                version: connectorVersion,
            }))
        }
        if (JSON.stringify(currentMappings) !== JSON.stringify(savedMappings)) {
            const payload = currentMappings.length > 0 ? currentMappings : null
            tasks.push(mutation.mutateAsync(payload))
        }
        try {
            await Promise.all(tasks)
            setSavedMappings(currentMappings)
        } catch {
            // Mutations surface their own toasts; nothing to do here.
        }
    }

    const handleCancel = () => {
        setCurrentMappings(savedMappings)
        setCreateParty(initialCreateParty)
        setCreateIdentity(initialCreateIdentity)
        setCreateAccount(initialCreateAccount)
        setPartyTypeCodeState(partyTypeCode)
        setIdentityTypeCodeState(identityTypeCode)
        setExternalIdFieldState(initialExternalIdField)
        setLifecycleSourceFieldState(initialLifecycleSourceField)
        setLifecycleEnabledValueState(initialLifecycleEnabledValue)
    }

    const atMaxMappings = currentMappings.length >= MAX_MAPPINGS

    // Read-only path
    if (!canUpdate) {
        return <InboundAttributeMappingReadOnly attributeMappings={savedMappings.length > 0 ? savedMappings : null}/>
    }

    const partyEntries = currentMappings
        .map((m, i) => ({m, i}))
        .filter(({m}) => m.targetEntityType === TargetEntityType.PARTY)
    const identityEntries = currentMappings
        .map((m, i) => ({m, i}))
        .filter(({m}) => m.targetEntityType === TargetEntityType.IDENTITY)

    const renderRow = (mapping: InboundAttributeMapping, idx: number, showIdentifier: boolean) => {
        const errors = rowErrors[idx] ?? {}
        return (
            <TableRow key={idx} data-testid={`mapping-row-${idx}`}>
                <TableCell className="align-top">
                    <Select
                        value={mapping.type ?? InboundMappingType.DIRECT}
                        onValueChange={(val) => handleTypeChange(idx, val)}
                    >
                        <SelectTrigger data-testid={`select-type-${idx}`} className="h-9">
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            <EnumSelectItems enumClass={InboundMappingTypeBadge}/>
                        </SelectContent>
                    </Select>
                </TableCell>
                <TableCell className="align-top">
                    <Input
                        value={mapping.source}
                        onChange={(e) => updateMapping(idx, {source: e.target.value})}
                        placeholder={
                            mapping.type === InboundMappingType.COMPUTED
                                ? i18n.inboundAttributeMappings.sourceHintComputed
                                : mapping.type === InboundMappingType.STATIC
                                    ? i18n.inboundAttributeMappings.sourceHintStatic
                                    : i18n.inboundAttributeMappings.sourceHint
                        }
                        data-testid={`input-source-${idx}`}
                        className={errors.source ? 'border-destructive' : ''}
                    />
                    {errors.source && (
                        <MutedText size="xs" className="text-destructive mt-1" data-testid={`error-source-${idx}`}>
                            {errors.source}
                        </MutedText>
                    )}
                </TableCell>
                <TableCell className="align-top">
                    <Input
                        value={mapping.target}
                        onChange={(e) => updateMapping(idx, {target: e.target.value})}
                        placeholder={i18n.inboundAttributeMappings.targetHint}
                        data-testid={`input-target-${idx}`}
                        // eslint-disable-next-line i18next/no-literal-string -- DOM id, not user-visible text
                        list={`target-options-${idx}`}
                        className={errors.target ? 'border-destructive' : ''}
                    />
                    <datalist id={`target-options-${idx}`}>
                        {(mapping.targetEntityType === TargetEntityType.PARTY
                            ? partyDefs
                            : mapping.targetEntityType === TargetEntityType.IDENTITY && !mapping.identifierType
                                ? identityDefs
                                : []
                        ).map((def) => (
                            <option key={def.name} value={def.name}>{def.displayName}</option>
                        ))}
                    </datalist>
                    {errors.target && (
                        <MutedText size="xs" className="text-destructive mt-1" data-testid={`error-target-${idx}`}>
                            {errors.target}
                        </MutedText>
                    )}
                    {!errors.target && mapping.target && !isTargetInSchema(mapping) && (
                        <MutedText size="xs" className="text-amber-600 mt-1 flex items-center gap-1" data-testid={`warning-target-${idx}`}>
                            <AlertTriangle className="h-3 w-3"/>
                            {i18n.inboundAttributeMappings.targetNotInSchema({
                                typeCode: mapping.targetEntityType === TargetEntityType.PARTY ? partyTypeCode : identityTypeCode,
                            })}
                        </MutedText>
                    )}
                </TableCell>
                {showIdentifier && (
                    <TableCell className="align-top">
                        {mapping.identifierType !== null ? (
                            <Select
                                value={mapping.identifierType || undefined}
                                onValueChange={(val) => updateMapping(idx, {identifierType: val})}
                            >
                                <SelectTrigger data-testid={`select-identifier-type-${idx}`} className="h-9">
                                    <SelectValue placeholder={i18n.inboundAttributeMappings.identifierTypePlaceholder}/>
                                </SelectTrigger>
                                <SelectContent>
                                    {identifierTypeOptions.map((t) => (
                                        <SelectItem key={t.code} value={t.code}>{labelForType(t)}</SelectItem>
                                    ))}
                                    {/* Preserve free-typed legacy values not in the catalog */}
                                    {mapping.identifierType && !identifierTypeOptions.some((t) => t.code === mapping.identifierType) && (
                                        <SelectItem value={mapping.identifierType}>{mapping.identifierType}</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        ) : (
                            <MutedText size="xs">—</MutedText>
                        )}
                    </TableCell>
                )}
                <TableCell className="align-top">
                    <Select
                        value={mapping.transform ?? TRANSFORM_NONE}
                        onValueChange={(val) =>
                            updateMapping(idx, {transform: val === TRANSFORM_NONE ? null : val, transformArgs: null})}
                    >
                        <SelectTrigger data-testid={`select-transform-${idx}`} className="h-9">
                            <SelectValue placeholder={i18n.inboundAttributeMappings.transformNone}/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={TRANSFORM_NONE}>{i18n.inboundAttributeMappings.transformNone}</SelectItem>
                            <EnumSelectItems enumClass={AttributeMappingTransformBadge}/>
                        </SelectContent>
                    </Select>
                    <TransformParamsFields
                        transform={mapping.transform}
                        args={mapping.transformArgs}
                        onChange={(a) => updateMapping(idx, {transformArgs: a})}
                        testIdPrefix={`transform-${idx}`}
                    />
                </TableCell>
                <TableCell className="align-top text-center">
                    <Checkbox
                        id={`required-${idx}`}
                        checked={mapping.required}
                        onCheckedChange={(checked) => updateMapping(idx, {required: checked === true})}
                        data-testid={`checkbox-required-${idx}`}
                        className="mt-2"
                    />
                </TableCell>
                <TableCell className="align-top">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => removeMapping(idx)}
                        data-testid={`btn-remove-mapping-${idx}`}
                        aria-label={i18n.inboundAttributeMappings.confirmDelete}
                    >
                        <Trash2 className="h-4 w-4"/>
                    </Button>
                </TableCell>
            </TableRow>
        )
    }

    const renderHeader = (showIdentifier: boolean) => (
        <TableHeader>
            <TableRow>
                <TableHead className="w-24" data-testid="col-type">{i18n.inboundAttributeMappings.type}</TableHead>
                <TableHead data-testid="col-source">{i18n.inboundAttributeMappings.source}</TableHead>
                <TableHead data-testid="col-target">{i18n.inboundAttributeMappings.target}</TableHead>
                {showIdentifier && (
                    <TableHead className="w-32" data-testid="col-identifier-type">{i18n.inboundAttributeMappings.identifierType}</TableHead>
                )}
                <TableHead className="w-36" data-testid="col-transform">{i18n.inboundAttributeMappings.transform}</TableHead>
                <TableHead className="w-16 text-center" data-testid="col-required">{i18n.inboundAttributeMappings.required}</TableHead>
                <TableHead className="w-10" data-testid="col-actions"/>
            </TableRow>
        </TableHeader>
    )

    return (
        <div className="space-y-4" data-testid="inbound-attribute-mapping-editor">
            {/* Section: Party */}
            <div className="rounded-md border p-4 space-y-3" data-testid="section-party">
                <div className="flex items-start gap-3" data-testid="toggle-create-party-label">
                    <Checkbox
                        checked={createParty}
                        onCheckedChange={(v) => {
                            const checked = v === true
                            setCreateParty(checked)
                            if (!checked) {
                                setCreateIdentity(false)
                                setCreateAccount(false)
                            }
                        }}
                        data-testid="toggle-create-party"
                        className="mt-1"
                    />
                    <div className="flex-1">
                        <div className="text-sm font-medium">{i18n.inboundAttributeMappings.createPartyToggleHeader}</div>
                        <MutedText size="xs">{i18n.inboundAttributeMappings.createPartyHint}</MutedText>
                    </div>
                </div>
                {createParty && (
                    <div className="ml-7 space-y-3">
                        <div className="max-w-xs">
                            <Select value={partyTypeCodeState} onValueChange={setPartyTypeCodeState}>
                                <SelectTrigger data-testid="select-party-type" className="h-8">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {partyTypeOptions.map((t) => (
                                        <SelectItem key={t.code} value={t.code}>{labelForType(t)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {partyEntries.length > 0 ? (
                            <Table>
                                {renderHeader(false)}
                                <TableBody>
                                    {partyEntries.map(({m, i}) => renderRow(m, i, false))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText size="sm" className="italic" data-testid="empty-party">
                                {i18n.inboundAttributeMappings.noPartyMappings}
                            </MutedText>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addPartyMapping}
                            disabled={atMaxMappings}
                            data-testid="btn-add-party-mapping"
                            title={atMaxMappings ? i18n.inboundAttributeMappings.maxMappingsTooltip : undefined}
                        >
                            <Plus className="h-4 w-4 mr-1"/>
                            {i18n.inboundAttributeMappings.addPartyMapping}
                        </Button>
                    </div>
                )}
            </div>

            {/* Section: Identity */}
            <div className="rounded-md border p-4 space-y-3" data-testid="section-identity">
                <div className="flex items-start gap-3" data-testid="toggle-create-identity-label">
                    <Checkbox
                        checked={createIdentity}
                        onCheckedChange={(v) => {
                            const checked = v === true
                            setCreateIdentity(checked)
                            if (!checked) setCreateAccount(false)
                        }}
                        disabled={!createParty}
                        data-testid="toggle-create-identity"
                        className="mt-1"
                    />
                    <div className="flex-1">
                        <div className="text-sm font-medium">{i18n.inboundAttributeMappings.createIdentityToggleHeader}</div>
                        <MutedText size="xs">{i18n.inboundAttributeMappings.createIdentityHint}</MutedText>
                    </div>
                </div>
                {createIdentity && (
                    <div className="ml-7 space-y-3">
                        <div className="max-w-xs">
                            <Select value={identityTypeCodeState} onValueChange={setIdentityTypeCodeState}>
                                <SelectTrigger data-testid="select-identity-type" className="h-8">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {identityTypeOptions.map((t) => (
                                        <SelectItem key={t.code} value={t.code}>{labelForType(t)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {identityEntries.length > 0 ? (
                            <Table>
                                {renderHeader(true)}
                                <TableBody>
                                    {identityEntries.map(({m, i}) => renderRow(m, i, true))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText size="sm" className="italic" data-testid="empty-identity">
                                {i18n.inboundAttributeMappings.noIdentityMappings}
                            </MutedText>
                        )}
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={addIdentityAttribute}
                                disabled={atMaxMappings}
                                data-testid="btn-add-identity-attribute"
                                title={atMaxMappings ? i18n.inboundAttributeMappings.maxMappingsTooltip : undefined}
                            >
                                <Plus className="h-4 w-4 mr-1"/>
                                {i18n.inboundAttributeMappings.addIdentityAttribute}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={addIdentityIdentifier}
                                disabled={atMaxMappings}
                                data-testid="btn-add-identity-identifier"
                                title={atMaxMappings ? i18n.inboundAttributeMappings.maxMappingsTooltip : undefined}
                            >
                                <Plus className="h-4 w-4 mr-1"/>
                                {i18n.inboundAttributeMappings.addIdentityIdentifier}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Section: Account */}
            <div className="rounded-md border p-4 space-y-3" data-testid="section-account">
                <div className="flex items-start gap-3" data-testid="toggle-create-account-label">
                    <Checkbox
                        checked={createAccount}
                        onCheckedChange={(v) => setCreateAccount(v === true)}
                        disabled={!createIdentity}
                        data-testid="toggle-create-account"
                        className="mt-1"
                    />
                    <div className="flex-1">
                        <div className="text-sm font-medium">{i18n.inboundAttributeMappings.createAccountToggle}</div>
                        <MutedText size="xs">{i18n.inboundAttributeMappings.createAccountHint}</MutedText>
                    </div>
                </div>
                {createAccount && (
                    <div className="ml-7 max-w-md space-y-3">
                        <div>
                            <label className="text-xs font-medium block mb-1">{i18n.inboundAttributeMappings.externalIdFieldLabel}</label>
                            <Input
                                value={externalIdFieldState}
                                onChange={(e) => setExternalIdFieldState(e.target.value)}
                                placeholder={i18n.inboundAttributeMappings.externalIdFieldPlaceholder}
                                data-testid="input-external-id-field"
                            />
                            <MutedText size="xs" className="mt-1">{i18n.inboundAttributeMappings.externalIdFieldHint}</MutedText>
                        </div>
                        <div>
                            <label className="text-xs font-medium block mb-1">{i18n.inboundAttributeMappings.lifecycleSourceFieldLabel}</label>
                            <Input
                                value={lifecycleSourceFieldState}
                                onChange={(e) => setLifecycleSourceFieldState(e.target.value)}
                                placeholder={i18n.inboundAttributeMappings.lifecycleSourceFieldPlaceholder}
                                data-testid="input-lifecycle-source-field"
                            />
                            <MutedText size="xs" className="mt-1">{i18n.inboundAttributeMappings.lifecycleSourceFieldHint}</MutedText>
                        </div>
                        {lifecycleSourceFieldState.trim() !== '' && (
                            <div>
                                <label className="text-xs font-medium block mb-1">{i18n.inboundAttributeMappings.lifecycleEnabledValueLabel}</label>
                                <Input
                                    value={lifecycleEnabledValueState}
                                    onChange={(e) => setLifecycleEnabledValueState(e.target.value)}
                                    placeholder={i18n.inboundAttributeMappings.lifecycleEnabledValuePlaceholder}
                                    data-testid="input-lifecycle-enabled-value"
                                />
                                <MutedText size="xs" className="mt-1">{i18n.inboundAttributeMappings.lifecycleEnabledValueHint}</MutedText>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer: Save/Cancel when dirty */}
            {isDirty && (
                <div className="flex items-center justify-end gap-2 pt-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCancel}
                        disabled={isSaving}
                        data-testid="btn-cancel-mappings"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        onClick={handleSave}
                        disabled={hasErrors || isSaving}
                        data-testid="btn-save-mappings"
                    >
                        {isSaving ? i18n.common.saving : i18n.common.saveChanges}
                    </Button>
                </div>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Card wrapper
// ---------------------------------------------------------------------------

interface InboundAttributeMappingCardProps {
    connectorId: string
    attributeMappings: InboundAttributeMapping[] | null
    canUpdate: boolean
    partyTypeCode: string
    identityTypeCode: string
    createParty: boolean
    createIdentity: boolean
    createAccount: boolean
    externalIdField: string
    lifecycleSourceField: string
    lifecycleEnabledValue: string
    connectorVersion: number
    currentSettings: InboundConnectorSettings
    onDirtyChange?: (dirty: boolean) => void
}

export function InboundAttributeMappingCard({
    connectorId,
    attributeMappings,
    canUpdate,
    partyTypeCode,
    identityTypeCode,
    createParty,
    createIdentity,
    createAccount,
    externalIdField,
    lifecycleSourceField,
    lifecycleEnabledValue,
    connectorVersion,
    currentSettings,
    onDirtyChange,
}: InboundAttributeMappingCardProps) {
    const i18n = useI18n()

    return (
        <Card data-testid="inbound-attribute-mapping-card">
            <RowCardHeader>
                <div>
                    <CardTitle className="flex items-center gap-2" data-testid="card-title-inbound-attribute-mappings">
                        <Settings2 className="h-5 w-5"/>
                        {i18n.inboundAttributeMappings.title}
                    </CardTitle>
                    <CardDescription>
                        {i18n.inboundAttributeMappings.description}
                        <br/>
                        <span>{i18n.inboundAttributeMappings.descriptionDetail}</span>
                    </CardDescription>
                </div>
            </RowCardHeader>
            <CardContent>
                {canUpdate ? (
                    <InboundAttributeMappingEditor
                        connectorId={connectorId}
                        attributeMappings={attributeMappings}
                        canUpdate={canUpdate}
                        partyTypeCode={partyTypeCode}
                        identityTypeCode={identityTypeCode}
                        createParty={createParty}
                        createIdentity={createIdentity}
                        createAccount={createAccount}
                        externalIdField={externalIdField}
                        lifecycleSourceField={lifecycleSourceField}
                        lifecycleEnabledValue={lifecycleEnabledValue}
                        connectorVersion={connectorVersion}
                        currentSettings={currentSettings}
                        onDirtyChange={onDirtyChange}
                    />
                ) : (
                    <InboundAttributeMappingReadOnly attributeMappings={attributeMappings}/>
                )}
            </CardContent>
        </Card>
    )
}
